// Base Swiper functionality - shared by all slider/slideshow components
class SwiperBase {
  constructor(element, options = {}) {
    this.element = element;
    this.options = options;
    this.swiper = null;
    this.init();
  }

  // Base configuration shared by all components
  getBaseConfig(swiperEl) {
    // Read responsive configuration from data attributes
    const slidesPerViewDesktop = swiperEl.dataset.slidesPerViewDesktop;
    const slidesPerViewMobile = swiperEl.dataset.slidesPerViewMobile;
    const spaceBetweenMobile = parseInt(swiperEl.dataset.spaceBetweenMobile) || 0;
    const spaceBetweenDesktop = parseInt(swiperEl.dataset.spaceBetweenDesktop) || spaceBetweenMobile;

    // Read conditional enabling flags
    const disableOnDesktop = swiperEl.dataset.disableOnDesktop === 'true';
    const disableOnMobile = swiperEl.dataset.disableOnMobile === 'true';

    // Check if this is a slideshow wrapper (exclude from mobile offsets)
    const isSlideshow = this.element.classList.contains('swiper-slideshow-wrapper');

    const config = {
      // Core settings
      loop: false,
      effect: 'slide',
      speed: 300,
      spaceBetween: spaceBetweenMobile,
      slidesPerView: slidesPerViewMobile, // Default to mobile

      // Mobile offset for bleed effect (non-slideshow carousels only)
      slidesOffsetBefore: !isSlideshow ? 16 : 0, // Default mobile offset
      slidesOffsetAfter: !isSlideshow ? 16 : 0,

      // Navigation
      navigation: {
        nextEl: this.element.querySelector('.swiper-button-next'),
        prevEl: this.element.querySelector('.swiper-button-prev')
      },

      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        paginationBulletMessage: 'Go to slide {{index}}'
      },

      // Callbacks
      on: {
        init: (swiper) => this.onSwiperInit(swiper),
        slideChange: (swiper) => this.onSlideChange(swiper)
      }
    };

    // Handle conditional enabling based on viewport
    if (disableOnDesktop || disableOnMobile) {
      config.breakpoints = {
        // Mobile breakpoint (below 750px)
        0: {
          init: !disableOnMobile,
          slidesPerView: slidesPerViewMobile,
          spaceBetween: spaceBetweenMobile,
          slidesOffsetBefore: !isSlideshow ? 16 : 0,
          slidesOffsetAfter: !isSlideshow ? 16 : 0
        },
        // Desktop breakpoint (750px and above)
        750: {
          init: !disableOnDesktop,
          slidesPerView: slidesPerViewDesktop,
          spaceBetween: spaceBetweenDesktop,
          slidesOffsetBefore: 0, // No offset on desktop
          slidesOffsetAfter: 0
        }
      };
    } else {
      // Normal responsive breakpoints for always-enabled sliders
      config.breakpoints = {
        750: {
          slidesPerView: slidesPerViewDesktop,
          spaceBetween: spaceBetweenDesktop,
          slidesOffsetBefore: 0, // No offset on desktop
          slidesOffsetAfter: 0
        }
      };
    }

    return config;
  }

  // Initialize with feature flags
  init() {
    const swiperEl = this.element.querySelector('.swiper');
    if (!swiperEl) return;

    const config = this.getBaseConfig(swiperEl);

    // Apply feature flags
    this.applyFeatures(config, swiperEl);

    // Initialize Swiper
    this.swiper = new Swiper(swiperEl, config);

    // Store reference
    swiperEl.swiperInstance = this.swiper;

    return this.swiper;
  }

  // Override this in child classes to add features
  applyFeatures(config, swiperEl) {
    // Base implementation - no additional features
  }

  // Base callbacks
  onSwiperInit(swiper) {
    // Set initial navigation state
    this.updateNavigationState(swiper);
    
    // Apply cascade animation order to slides with data-cascade attribute
    this.applyCascadeOrder(swiper);
    
    // Show the carousel now that it's initialized
    const wrapper = swiper.el.closest('.swiper-wrapper-base');
    if (wrapper) {
      wrapper.classList.add('swiper-initialized');
    }
  }
  
  // Apply cascade animation order to swiper slides
  applyCascadeOrder(swiper) {
    // Check if the wrapper has cascade animation enabled
    const wrapper = swiper.el.closest('.swiper-wrapper-base');
    if (!wrapper || wrapper.dataset.cascadeAnimation !== 'true') {
      return;
    }
    
    // Get the number of visible slides based on current viewport
    let slidesPerView = swiper.params.slidesPerView;
    
    // Handle "auto" slidesPerView - count visible slides in viewport
    if (slidesPerView === 'auto') {
      const swiperWidth = swiper.el.offsetWidth;
      let visibleCount = 0;
      let accumulatedWidth = 0;
      
      Array.from(swiper.slides).forEach(slide => {
        const slideWidth = slide.offsetWidth;
        const spaceBetween = swiper.params.spaceBetween || 0;
        
        if (accumulatedWidth < swiperWidth) {
          visibleCount++;
          accumulatedWidth += slideWidth + spaceBetween;
        }
      });
      
      slidesPerView = visibleCount;
    } else {
      slidesPerView = Math.ceil(slidesPerView);
    }
    
    const slides = Array.from(swiper.slides);
    
    slides.forEach((slide, index) => {
      // Add animation classes to all slides
      slide.classList.add('scroll-trigger', 'animate--slide-in');
      
      // Only apply cascade to the first visible slides
      if (index < slidesPerView) {
        slide.setAttribute('data-cascade', '');
        slide.setAttribute('data-animation-order', index);
      }
    });
    
    // Re-apply cascade on window resize to handle responsive slidesPerView changes
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        let newSlidesPerView = swiper.params.slidesPerView;
        
        // Handle "auto" slidesPerView
        if (newSlidesPerView === 'auto') {
          const swiperWidth = swiper.el.offsetWidth;
          let visibleCount = 0;
          let accumulatedWidth = 0;
          
          Array.from(swiper.slides).forEach(slide => {
            const slideWidth = slide.offsetWidth;
            const spaceBetween = swiper.params.spaceBetween || 0;
            
            if (accumulatedWidth < swiperWidth) {
              visibleCount++;
              accumulatedWidth += slideWidth + spaceBetween;
            }
          });
          
          newSlidesPerView = visibleCount;
        } else {
          newSlidesPerView = Math.ceil(newSlidesPerView);
        }
        
        // Remove cascade from all slides first
        slides.forEach(slide => {
          slide.removeAttribute('data-cascade');
          slide.removeAttribute('data-animation-order');
        });
        
        // Re-apply to visible slides
        slides.forEach((slide, index) => {
          if (index < newSlidesPerView) {
            slide.setAttribute('data-cascade', '');
            slide.setAttribute('data-animation-order', index);
          }
        });
      }, 250);
    };
    
    window.addEventListener('resize', handleResize);
  }

  onSlideChange(swiper) {
    // Update counter if it exists
    const currentCounter = swiper.el.querySelector('.slider-counter--current');
    const totalCounter = swiper.el.querySelector('.slider-counter--total');

    if (currentCounter) {
      currentCounter.textContent = swiper.realIndex + 1;
    }
    if (totalCounter) {
      totalCounter.textContent = swiper.slides.length;
    }

    // Update navigation state classes
    this.updateNavigationState(swiper);
  }

  // Update navigation state classes for gradient effects
  updateNavigationState(swiper) {
    const wrapper = swiper.el.closest('.swiper-wrapper-base');
    if (!wrapper) return;

    // Remove existing state classes
    wrapper.classList.remove('swiper-at-beginning', 'swiper-at-end', 'swiper-in-middle');

    // Add appropriate state class
    if (swiper.isBeginning) {
      wrapper.classList.add('swiper-at-beginning');
    } else if (swiper.isEnd) {
      wrapper.classList.add('swiper-at-end');
    } else {
      wrapper.classList.add('swiper-in-middle');
    }

    // Also add data attributes for more flexible CSS targeting
    wrapper.setAttribute('data-swiper-beginning', swiper.isBeginning);
    wrapper.setAttribute('data-swiper-end', swiper.isEnd);
    wrapper.setAttribute('data-swiper-index', swiper.realIndex);
    wrapper.setAttribute('data-swiper-total', swiper.slides.length);
  }

  // Cleanup
  destroy() {
    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = null;
    }
  }
}

// Export for use by other components
window.SwiperBase = SwiperBase;

// Initialize all swiper wrappers when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const swiperWrappers = document.querySelectorAll('.swiper-wrapper-base');

  swiperWrappers.forEach(wrapper => {
    new SwiperBase(wrapper);
  });
});

document.addEventListener('shopify:section:load', function(event) {
  const swiperWrappers = event.target.querySelectorAll('.swiper-wrapper-base');
  swiperWrappers.forEach(wrapper => {
    new SwiperBase(wrapper);
  });
});

document.addEventListener('shopify:section:unload', function(event) {
  const swiperWrappers = event.target.querySelectorAll('.swiper-wrapper-base');
  swiperWrappers.forEach(wrapper => {
    const swiperEl = wrapper.querySelector('.swiper');
    if (swiperEl && swiperEl.swiperInstance) {
      swiperEl.swiperInstance.destroy();
    }
  });
});